import { useEffect, useState, useCallback } from "react";
import { Download, Share, X } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { log } from "../lib/log";

const DISMISS_KEY = "pwa_install_dismissed_v2";

const isIOS = () =>
  typeof window !== "undefined" &&
  /iPad|iPhone|iPod/.test(window.navigator.userAgent || "") &&
  !/CriOS|FxiOS|EdgiOS/i.test(window.navigator.userAgent || "");

const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

// Surfaces a small bottom-anchored card that lets the user pin the
// app to their home screen. On Android / Desktop Chrome we capture
// the `beforeinstallprompt` event and trigger the native dialog when
// the button is tapped. On iOS Safari we render manual instructions
// (Apple still refuses to expose the install API) — share → "Add to
// Home Screen".
export default function PWAInstallPrompt() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const [deferred, setDeferred] = useState(null);
  const [mode, setMode] = useState(null); // "android" | "ios" | null
  const [open, setOpen] = useState(false);

  const dismiss = useCallback(() => {
    try {
      // Stores ONLY a timestamp of the dismissal — not user data,
      // credentials, or anything sensitive. Used to hide the install
      // banner for 7 days after the user closes it. Safe to keep in
      // localStorage (no XSS exposure risk).
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (e) {
      // Private-browsing mode (Safari) throws on setItem.
      // Non-fatal — the banner will simply re-appear next visit.
      log.warn("[PWA] dismissal could not be persisted:", e?.message || e);
    }
    setOpen(false);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return; // already installed
    const last = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (last && Date.now() - last < 7 * 24 * 3600 * 1000) return; // hide for 7 days

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setMode("android");
      // Defer the visible nudge by a few seconds so it doesn't fight
      // the page's hero animations.
      setTimeout(() => setOpen(true), 3500);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    if (isIOS()) {
      setMode("ios");
      setTimeout(() => setOpen(true), 5000);
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    setOpen(false);
    if (outcome === "dismissed") dismiss();
  };

  if (!open || !mode) return null;

  const t = isAr
    ? {
        title: "ثبّت تطبيق مجموعة الدخيل القانونية على هاتفك",
        body:
          "افتح موقع مجموعة الدخيل القانونية كتطبيق سريع من شاشتك الرئيسية، دون الحاجة إلى المتصفح.",
        installBtn: "تثبيت الآن",
        iosHint: "اضغط زرّ المشاركة",
        iosThen: "ثم اختر «أضف إلى الشاشة الرئيسية»",
        dismiss: "ليس الآن",
      }
    : {
        title: "Install the Sami app on your phone",
        body:
          "Open Aldakheel Legal as a fast standalone app from your home screen — no browser bar.",
        installBtn: "Install",
        iosHint: "Tap the Share button",
        iosThen: 'Then choose "Add to Home Screen"',
        dismiss: "Not now",
      };

  return (
    <div
      data-testid="pwa-install-prompt"
      dir={isAr ? "rtl" : "ltr"}
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-[380px] z-[60] border border-slate-200 bg-white shadow-xl"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="shrink-0 w-12 h-12 bg-slate-900 flex items-center justify-center">
          <img
            src="/branding/icon-192.png"
            alt=""
            className="w-9 h-9 object-contain"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-display text-lg tracking-tight leading-snug">
            {t.title}
          </h4>
          <p className="text-[13px] text-slate-600 leading-snug mt-1">
            {t.body}
          </p>
          {mode === "ios" ? (
            <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
              <Share className="w-3 h-3" /> {t.iosHint}.{" "}
              <span className="text-slate-700">{t.iosThen}.</span>
            </p>
          ) : (
            <div className="mt-3 flex items-center gap-2">
              <button
                data-testid="pwa-install-button"
                onClick={install}
                className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-xs uppercase tracking-[0.18em] px-3.5 py-2 hover:bg-slate-700 transition-colors"
              >
                <Download className="w-3 h-3" /> {t.installBtn}
              </button>
              <button
                onClick={dismiss}
                className="text-xs text-slate-500 hover:text-slate-700 px-2 py-2"
              >
                {t.dismiss}
              </button>
            </div>
          )}
        </div>
        <button
          onClick={dismiss}
          aria-label="dismiss"
          data-testid="pwa-install-dismiss"
          className="shrink-0 text-slate-400 hover:text-slate-700 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
